import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, CircularProgress, Divider, FormControl, Grid, TextField, Typography, Autocomplete, FormHelperText, } from "@mui/material";
import { FloppyDisk } from "@phosphor-icons/react";
import { useEffect, useState, useContext, Fragment, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import LeadDetailApi from "../../../infrastructure/api/pages/lead-detail-api";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { UserRolesPermissionsContext } from "../../../context/UserRolesPermissionsContext";
import { checkCreateEdit, } from "../../../application/common/utils/roles-helper";
import { Modules } from "../../../application/common/enums/screen-name";
import LeadsObjectApi from "../../../infrastructure/api/pages/lead-object-api";
import { useDebouncedFetch } from "../../hooks/useDebouncedFetch";
const initialFormData = {
    id: "",
    name: "",
    safeId: "",
    chamberOfCommerce: "",
    street: "",
    houseNumber: "",
    addition: "",
    postalCode: "",
    city: "",
    country: "",
    phoneNumber: "",
    email: "",
    btwNumber: "",
    locationId: "",
    location: null,
};
const CompanyInfoTab = () => {
    var _a, _b;
    const { t: translate } = useTranslation();
    const validationSchema = yup.object({
        name: yup.string().required(translate("NameRequired")),
        // Pre-existing bug (found via testing, unrelated to Safe-ID): a company whose email was
        // never set comes back from the API as `null`, and yup's plain .email() schema rejects
        // null outright ("email cannot be null") — blocking Save entirely for that company, on ANY
        // field, not just email. .nullable() lets an absent email through while still validating a
        // present-but-malformed one.
        email: yup.string().email(translate("InvalidEmail")).nullable(),
    });
    const urlParams = new URLSearchParams(window.location.search);
    const { reset, register, handleSubmit, setValue, control, watch, formState: { errors }, } = useForm({
        resolver: yupResolver(validationSchema),
    });
    const [data, setData] = useState(initialFormData);
    const [saving, setSaving] = useState(false);
    const { RolesPermissions } = useContext(UserRolesPermissionsContext);
    const [locations, setLocations] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const personSelectRef = useRef(null);
    const [error, setError] = useState(undefined);
    // Safe-ID uniqueness (item #12). The Safe-ID is typed here — not at lead creation — so this
    // is where the duplicate has to be caught. A blank Safe-ID is allowed: a manually created
    // lead starts with an empty company and the Safe-ID is filled in later.
    const companyId = data.id || urlParams.get("companyId") || undefined;
    const safeIdValue = watch("safeId");
    const trimmedSafeId = (safeIdValue !== null && safeIdValue !== void 0 ? safeIdValue : "").trim();
    const storedSafeId = ((_a = data.safeId) !== null && _a !== void 0 ? _a : "").trim();
    // Only a changed Safe-ID is checked, matching the server. Companies that already share a
    // Safe-ID from before this rule existed stay editable — otherwise their address could not
    // be corrected either — while a newly typed duplicate is still refused.
    const safeIdChanged = trimmedSafeId.toLowerCase() !== storedSafeId.toLowerCase();
    const { data: safeIdExists, loading: checkingSafeId } = useDebouncedFetch({
        fetcher: (config) => LeadDetailApi.isSafeIdExist(trimmedSafeId, companyId, config),
        deps: [trimmedSafeId, companyId, safeIdChanged],
        isActive: trimmedSafeId.length > 0 && safeIdChanged,
    });
    const isDuplicateSafeId = safeIdExists === true;
    const onSubmit = (data) => {
        var _a, _b;
        if (isDuplicateSafeId) {
            toast.error(translate("SafeIdAlreadyExists"));
            return;
        }
        if (!data.location) {
            setError("Verplicht");
            (_a = personSelectRef === null || personSelectRef === void 0 ? void 0 : personSelectRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (data.location) {
            setSaving(true);
            // Trimmed on the way out, but a blank stays blank rather than becoming "" — the column
            // holds NULL for companies whose Safe-ID has not been filled in yet.
            LeadDetailApi.updateCompanyInfo(Object.assign(Object.assign({}, data), { safeId: data.safeId ? trimmedSafeId : data.safeId }))
                .then((response) => {
                if (response.status === 200 && response.data != null) {
                    toast.success(translate("SavedSuccessfully"));
                }
            })
                .catch((err) => {
                var _a, _b, _c;
                // The server re-checks the Safe-ID, so a duplicate that slipped past the
                // debounced check (or a concurrent save) still surfaces here.
                toast.error((_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) !== null && _c !== void 0 ? _c : translate("ErrorWhileSavingCompany"));
            })
                .finally(() => {
                setSaving(false);
            });
        }
        else {
            setError("Verplicht");
            (_b = personSelectRef === null || personSelectRef === void 0 ? void 0 : personSelectRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    };
    useEffect(() => {
        const companyId = urlParams.get("companyId");
        if (companyId) {
            LeadDetailApi.getCompanyInfo(companyId)
                .then((response) => {
                if (response.status === 200 && response.data != null) {
                    if (response.data.locationId ||
                        response.data.street ||
                        response.data.city) {
                        const weergavenaam = [
                            response.data.street,
                            response.data.houseNumber,
                            response.data.addition,
                            response.data.postalCode,
                            response.data.city,
                        ]
                            .filter(Boolean)
                            .join(" ")
                            .replace(/\s+/g, " ")
                            .trim();
                        const location = {
                            id: response.data.locationId || "display-only",
                            weergavenaam: weergavenaam || response.data.locationId || "",
                            isFakeId: !response.data.locationId,
                        };
                        response.data.location = location;
                        setLocations([location]);
                    }
                    setData(response.data);
                    reset(response.data);
                    //setValue("name", response.data.name);
                }
            })
                .finally(() => {
                setIsLoading(false);
            });
        }
        else {
            setIsLoading(false);
        }
    }, []);
    const resetLocation = () => {
        setValue("street", "");
        setValue("houseNumber", "");
        setValue("addition", "");
        setValue("locationId", "");
        setValue("postalCode", "");
        setValue("city", "");
        setValue("locationId", "");
        setValue("location", null);
        setError(undefined);
    };
    const searchLocation = (query) => {
        if (query) {
            LeadsObjectApi.searchLocation(query).then((response) => {
                if (response.status === 200) {
                    const mappedLocations = response.data.response.docs.map((location) => ({
                        id: location === null || location === void 0 ? void 0 : location.id,
                        weergavenaam: location === null || location === void 0 ? void 0 : location.weergavenaam,
                        score: location === null || location === void 0 ? void 0 : location.score,
                        isFakeId: location === null || location === void 0 ? void 0 : location.isFakeId,
                    }));
                    setLocations(mappedLocations);
                    setIsFetching(false);
                }
            });
        }
        setIsFetching(false);
    };
    const getLocationById = (id) => {
        LeadsObjectApi.getLocationById(id).then((response) => {
            var _a, _b, _c, _d, _e, _f;
            if (response.status === 200) {
                const location = response.data.response.docs[0];
                setValue("street", (_a = location === null || location === void 0 ? void 0 : location.straatnaam) !== null && _a !== void 0 ? _a : "");
                setValue("houseNumber", (_c = (_b = location === null || location === void 0 ? void 0 : location.huisnummer) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : "");
                setValue("addition", `${(_d = location === null || location === void 0 ? void 0 : location.huisletter) !== null && _d !== void 0 ? _d : ""}${(location === null || location === void 0 ? void 0 : location.huisnummertoevoeging)
                    ? "-" + location.huisnummertoevoeging
                    : ""}`);
                setValue("locationId", location === null || location === void 0 ? void 0 : location.id);
                setValue("postalCode", (_e = location === null || location === void 0 ? void 0 : location.postcode) !== null && _e !== void 0 ? _e : "");
                setValue("city", (_f = location.woonplaatsnaam) !== null && _f !== void 0 ? _f : "");
                setValue("country", "Netherlands");
            }
        });
    };
    const fetchLocations = (query) => {
        setIsFetching(true);
        searchLocation(query);
    };
    const onLocationChange = (value) => {
        if (value && !value.isFakeId) {
            getLocationById(value.id);
        }
    };
    return (_jsx("form", { onSubmit: checkCreateEdit(RolesPermissions, Modules.Leads)
            ? handleSubmit(onSubmit)
            : undefined, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", margin: "normal", children: _jsxs(Box, { sx: {
                    padding: "var(--2, 16px) var(--4, 32px)"
                }, children: [_jsxs(Grid, { container: true, sx: {
                            justifyContent: "space-between",
                            alignItems: "center"
                        }, children: [_jsx(Typography, { variant: "h2", sx: { width: { xs: "100%", sm: "49%", md: "49%" } }, children: translate("CompanyInformation") }), checkCreateEdit(RolesPermissions, Modules.Leads) && (_jsx(Grid, { sx: { display: "flex", justifyContent: "flex-end" }, size: {
                                    xs: 12,
                                    sm: 6,
                                    md: 6
                                }, children: _jsxs(Button, { type: "submit", variant: "outlined", "data-testid": "company-save", 
                                    // Blocked while the Safe-ID is a known duplicate — saving is refused by the
                                    // server too, this just says so before the click.
                                    disabled: isDuplicateSafeId || saving, sx: {
                                        borderRadius: "12px",
                                        border: "var(--none, 1px) solid var(--n-primary, #130922)",
                                        background: "var(--n-primary-light, #F9F6FF)",
                                        display: "flex",
                                        padding: "12px !important",
                                        alignItems: "flex-start",
                                        gap: "10px",
                                        minWidth: "auto !important",
                                    }, children: ["Opslaan", saving ? (_jsx(CircularProgress, { size: 24, color: "inherit" })) : (_jsx(FloppyDisk, { size: 24 }))] }) }))] }), _jsxs(Grid, { container: true, sx: {
                            gap: { xs: 0.5, sm: 0.5, md: 1 },
                            flexDirection: { xs: "column", sm: "row", md: "row" }
                        }, children: [_jsx(Controller, { name: "name", control: control, defaultValue: data.name, render: ({ field }) => {
                                    var _a;
                                    return (_jsx(TextField, Object.assign({ label: translate("Name"), variant: "outlined", margin: "normal", sx: { width: { xs: "100%", sm: "100%", md: "100%" } } }, field, { error: !!errors.name, helperText: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message, slotProps: {
                                            htmlInput: { "data-testid": "company-name-input" }
                                        } })));
                                } }), _jsx(Controller, { name: "safeId", control: control, defaultValue: (_b = data.safeId) !== null && _b !== void 0 ? _b : "", render: ({ field }) => (_jsx(TextField, Object.assign({ label: translate("SafeId"), variant: "outlined", margin: "normal", sx: { width: { xs: "100%", sm: "49%", md: "49%" } } }, field, { error: isDuplicateSafeId, helperText: isDuplicateSafeId
                                        ? translate("SafeIdAlreadyExists")
                                        : checkingSafeId
                                            ? translate("CheckingSafeId")
                                            : undefined, slotProps: {
                                        htmlInput: { "data-testid": "company-safeid-input" },
                                        formHelperText: {
                                            // @ts-ignore - data-testid passthrough, same pattern as inputProps above
                                            "data-testid": "company-safeid-helper",
                                        }
                                    } }))) }), _jsx(Controller, { name: "chamberOfCommerce", control: control, defaultValue: data.chamberOfCommerce, render: ({ field }) => (_jsx(TextField, Object.assign({ label: translate("ChamberOfCommerce"), variant: "outlined", margin: "normal", sx: { width: { xs: "100%", sm: "49%", md: "49%" } } }, field))) })] }), _jsx(Divider, {}), _jsx(Grid, { container: true, sx: {
                            gap: 1,
                            flexDirection: { xs: "column", sm: "row", md: "row" }
                        }, children: isLoading ? null : (_jsx(Controller, { name: "location", control: control, defaultValue: data.location, rules: {
                                required: {
                                    value: true,
                                    message: "Locatie is vereist.",
                                },
                            }, render: ({ field: { onChange, value } }) => {
                                var _a;
                                return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", margin: "normal", children: [_jsx(Autocomplete, { isOptionEqualToValue: (option, value) => option.id === value.id, disablePortal: true, fullWidth: true, getOptionLabel: (option) => option.weergavenaam, options: locations, disabled: isLoading, loading: isFetching, onChange: (_, value) => {
                                                onLocationChange(value);
                                                onChange(value);
                                            }, value: value, noOptionsText: translate("noOptions"), renderInput: (params) => (_jsx(TextField, Object.assign({ placeholder: "Zoek locatie", error: !!error, helperText: error, inputRef: personSelectRef }, params, { label: "Locatie", onChange: (e) => {
                                                    fetchLocations(e.target.value);
                                                    resetLocation();
                                                }, slotProps: Object.assign(Object.assign({}, params.slotProps), { input: Object.assign(Object.assign({}, params.slotProps.input), { endAdornment: (_jsxs(Fragment, { children: [isFetching ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, params.slotProps.input.endAdornment] })) }) }) }))) }), _jsx(FormHelperText, { error: true, children: (_a = errors.location) === null || _a === void 0 ? void 0 : _a.message })] }));
                            } })) }), _jsx(Divider, {}), _jsxs(Grid, { container: true, sx: {
                            gap: 1,
                            flexDirection: { xs: "column", sm: "row", md: "row" }
                        }, children: [_jsx(Controller, { name: "phoneNumber", control: control, defaultValue: data.phoneNumber, render: ({ field }) => (_jsx(TextField, Object.assign({ label: translate("PhoneNumber"), variant: "outlined" }, register("phoneNumber"), { margin: "normal", sx: { width: { xs: "100%", sm: "49%", md: "49%" } } }, field))) }), _jsx(Controller, { name: "email", control: control, defaultValue: data.email, render: ({ field }) => {
                                    var _a;
                                    return (_jsx(TextField, Object.assign({ label: translate("Email"), variant: "outlined" }, register("email"), { margin: "normal", sx: { width: { xs: "100%", sm: "49%", md: "49%" } }, error: !!errors.email, helperText: (_a = errors.email) === null || _a === void 0 ? void 0 : _a.message }, field)));
                                } })] })] }) }) }));
};
export default CompanyInfoTab;
